// -----------------------------------------------------------------------------
// PORTFOLIO STUB FIXTURES
//
// These fixtures are the source of truth for the PortfolioResponse JSON shape
// while the backend is being built. When the real endpoints ship, delete these
// files and swap portfolio hooks to hit the API. The shape here MUST match the
// backend contract in docs/portfolio-api-contract.md exactly.
// -----------------------------------------------------------------------------

// A returning Academy learner's transcript-in-progress. Used for the current
// user's own portfolio view (Insights -> Portfolio tab).
export const LEARNER_PORTFOLIO_STUB = {
  studentId: 'self',
  displayName: null, // Frontend fills from auth user if null.
  avatarColor: null,
  academicYearStartMonth: 8, // August. Used to derive year boundaries.
  currentAcademicYear: '2025-2026',
  aggregate: {
    totalCoursesCompleted: 5,
    totalCoursesInProgress: 3,
    totalMinutesLearned: 3120,
    averageMasteryPercent: 88,
    firstActivityAt: '2025-09-04T14:12:00Z',
    lastActivityAt: '2026-07-05T10:30:00Z',
  },
  academicYears: [
    {
      year: '2025-2026',
      label: '2025–2026',
      startDate: '2025-08-01',
      endDate: '2026-07-31',
      isCurrent: true,
      subjects: [
        {
          subject: 'Mathematics',
          courses: [
            makeCourse({
              id: 'stub-pf-math-1',
              title: 'Pre-Algebra Review',
              difficulty: 'BEGINNER',
              status: 'COMPLETED',
              startedAt: '2025-09-04T14:12:00Z',
              completedAt: '2025-11-18T16:00:00Z',
              lessonCount: 10,
              completedLessonCount: 10,
              masteryPercent: 96,
              minutesLearned: 420,
              assignedByParent: false,
            }),
            makeCourse({
              id: 'stub-pf-math-2',
              title: 'Algebra 1',
              difficulty: 'BEGINNER',
              status: 'IN_PROGRESS',
              startedAt: '2025-12-01T14:00:00Z',
              completedAt: null,
              lessonCount: 8,
              completedLessonCount: 3,
              masteryPercent: 82,
              minutesLearned: 240,
              assignedByParent: true,
            }),
          ],
        },
        {
          subject: 'Science',
          courses: [
            makeCourse({
              id: 'stub-pf-sci-1',
              title: 'Life Science Fundamentals',
              difficulty: 'BEGINNER',
              status: 'COMPLETED',
              startedAt: '2025-09-10T14:00:00Z',
              completedAt: '2026-01-22T15:30:00Z',
              lessonCount: 12,
              completedLessonCount: 12,
              masteryPercent: 88,
              minutesLearned: 640,
              assignedByParent: false,
            }),
            makeCourse({
              id: 'stub-pf-sci-2',
              title: 'Physical Science',
              difficulty: 'INTERMEDIATE',
              status: 'IN_PROGRESS',
              startedAt: '2026-02-05T14:00:00Z',
              completedAt: null,
              lessonCount: 10,
              completedLessonCount: 3,
              masteryPercent: 76,
              minutesLearned: 320,
              assignedByParent: false,
            }),
          ],
        },
        {
          subject: 'English Language Arts',
          courses: [
            makeCourse({
              id: 'stub-pf-eng-1',
              title: 'Literary Analysis Foundations',
              difficulty: 'INTERMEDIATE',
              status: 'COMPLETED',
              startedAt: '2025-09-08T14:00:00Z',
              completedAt: '2025-12-15T15:00:00Z',
              lessonCount: 9,
              completedLessonCount: 9,
              masteryPercent: 91,
              minutesLearned: 540,
              assignedByParent: false,
            }),
          ],
        },
        {
          subject: 'History & Social Studies',
          courses: [
            makeCourse({
              id: 'stub-pf-hist-1',
              title: 'U.S. History to Reconstruction',
              difficulty: 'INTERMEDIATE',
              status: 'IN_PROGRESS',
              startedAt: '2026-01-08T14:00:00Z',
              completedAt: null,
              lessonCount: 14,
              completedLessonCount: 4,
              masteryPercent: 71,
              minutesLearned: 400,
              assignedByParent: true,
            }),
          ],
        },
      ],
    },
    {
      year: '2024-2025',
      label: '2024–2025',
      startDate: '2024-08-01',
      endDate: '2025-07-31',
      isCurrent: false,
      subjects: [
        {
          subject: 'Mathematics',
          courses: [
            makeCourse({
              id: 'stub-pf-math-prev',
              title: 'Foundations of Arithmetic',
              difficulty: 'BEGINNER',
              status: 'COMPLETED',
              startedAt: '2024-09-05T14:00:00Z',
              completedAt: '2025-01-18T15:00:00Z',
              lessonCount: 8,
              completedLessonCount: 8,
              masteryPercent: 92,
              minutesLearned: 320,
              assignedByParent: false,
            }),
          ],
        },
        {
          subject: 'Science',
          courses: [
            makeCourse({
              id: 'stub-pf-sci-prev',
              title: 'Earth Science Overview',
              difficulty: 'BEGINNER',
              status: 'COMPLETED',
              startedAt: '2024-09-12T14:00:00Z',
              completedAt: '2025-04-03T15:00:00Z',
              lessonCount: 10,
              completedLessonCount: 10,
              masteryPercent: 85,
              minutesLearned: 440,
              assignedByParent: false,
            }),
          ],
        },
      ],
    },
  ],
};

// Per-student portfolios keyed by studentId. Only stub students defined; unknown
// IDs fall back to the learner stub with the displayName overridden.
export const STUDENT_PORTFOLIO_STUBS = {
  'stub-student-emma': {
    ...LEARNER_PORTFOLIO_STUB,
    studentId: 'stub-student-emma',
    displayName: 'Emma Chen',
    avatarColor: '#7C3AED',
  },
  'stub-student-marcus': {
    studentId: 'stub-student-marcus',
    displayName: 'Marcus Rivera',
    avatarColor: '#0EA5E9',
    academicYearStartMonth: 8,
    currentAcademicYear: '2025-2026',
    aggregate: {
      totalCoursesCompleted: 1,
      totalCoursesInProgress: 2,
      totalMinutesLearned: 380,
      averageMasteryPercent: 74,
      firstActivityAt: '2026-04-02T14:00:00Z',
      lastActivityAt: '2026-06-17T08:14:00Z',
    },
    academicYears: [
      {
        year: '2025-2026',
        label: '2025–2026',
        startDate: '2025-08-01',
        endDate: '2026-07-31',
        isCurrent: true,
        subjects: [
          {
            subject: 'Mathematics',
            courses: [
              makeCourse({
                id: 'stub-pf-m1',
                title: 'Multiplication & Division Mastery',
                difficulty: 'BEGINNER',
                status: 'COMPLETED',
                startedAt: '2026-04-02T14:00:00Z',
                completedAt: '2026-05-20T15:00:00Z',
                lessonCount: 6,
                completedLessonCount: 6,
                masteryPercent: 82,
                minutesLearned: 180,
                assignedByParent: true,
              }),
              makeCourse({
                id: 'stub-pf-m2',
                title: 'Fractions & Decimals',
                difficulty: 'BEGINNER',
                status: 'IN_PROGRESS',
                startedAt: '2026-06-01T14:00:00Z',
                completedAt: null,
                lessonCount: 8,
                completedLessonCount: 1,
                masteryPercent: 68,
                minutesLearned: 120,
                assignedByParent: true,
              }),
            ],
          },
          {
            subject: 'Science',
            courses: [
              makeCourse({
                id: 'stub-pf-s1',
                title: 'Weather & Climate Basics',
                difficulty: 'BEGINNER',
                status: 'IN_PROGRESS',
                startedAt: '2026-05-10T14:00:00Z',
                completedAt: null,
                lessonCount: 7,
                completedLessonCount: 1,
                masteryPercent: 70,
                minutesLearned: 80,
                assignedByParent: false,
              }),
            ],
          },
        ],
      },
    ],
  },
};

function makeCourse(overrides) {
  const {
    id,
    title,
    difficulty,
    status,
    startedAt,
    completedAt,
    lessonCount,
    completedLessonCount,
    masteryPercent,
    minutesLearned,
    assignedByParent,
  } = overrides;
  return {
    courseId: id,
    title,
    subject: null, // Backend may populate; frontend inherits from grouping when null.
    difficulty,
    status, // 'IN_PROGRESS' | 'COMPLETED' | 'NOT_STARTED'
    startedAt,
    completedAt,
    lessonCount,
    completedLessonCount,
    masteryPercent, // 0-100, or null if no graded work yet
    minutesLearned,
    assignedByParent,
  };
}
